import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  CalendarCheck, MapPin, DollarSign, Loader2, Send, CheckCircle2,
  Sparkles, Instagram, ShieldCheck, Zap, Lock, Star, Users, MapPinned,
  Calendar, Clock, Building2, User as UserIcon, Mail, Phone, MessageSquare,
  Megaphone, Wine, Camera, HandHeart, MoreHorizontal, Plane,
} from 'lucide-react';
import CertificationBadges from '../components/CertificationBadges';
import { resolveProfilePhoto } from '../utils/profilePhoto';
import { toast } from 'sonner';
import LinkifiedText from '../components/LinkifiedText';
import PhotoLightbox from '../components/PhotoLightbox';
import { IEVNTUR_URL } from '../components/PoweredByFooter';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public booking page — /book/:slug
 *
 * Iter-291 (V284) — premium two-column layout (booking form on the left,
 * talent profile card on the right). The banner has intentionally been
 * removed from this surface; banner customization is still active on the
 * talent public profile + company / agency recruiting hubs.
 *
 * No auth required. Fetches the talent's public profile, then collects
 * event details from the client. On submit we either attach to an existing
 * user (matching email) or stub-create one and return a magic-link URL the
 * frontend surfaces for the client to claim their account.
 */
const PublicBooking = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [talent, setTalent] = useState(null);
  // Iter-P0-20260803 — Additional profile info rendered BELOW the existing
  // page. Never mutates the layout above. Loaded independently so a slow
  // stats query never blocks the primary booking form from rendering.
  const [profileStats, setProfileStats] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { request_id, magic_login_url }
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  // Iter-291 — Detect desktop breakpoint *once* on mount so we can render
  // only ONE layout (mobile single-stack vs desktop two-flex-columns) and
  // avoid the row-balancing gap that CSS Grid introduces when the two
  // columns have very different heights.
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  // Iter-291 — purely visual stepper. The form itself is single-page; this
  // tells the user *what* they're filling out (Event Details → Your Info →
  // Review & Send). Auto-advances as required fields are populated.
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    company_name: '',
    event_date: '',
    event_start_time: '',
    event_end_time: '',
    event_city: '',
    event_state: '',
    venue_address: '',
    event_details: '',
    hourly_rate: '',
    hours_estimated: '',
    roles_needed: [],      // Iter-291 — fixed 6-option selector (multi-select)
    role_details: '',      // Iter-291 — required when "Other Role Not Listed" is picked
  });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/booking/public/${slug}`);
      const raw = r.data || {};
      // Defensive `gig_roles` normalization — backend already does this, we
      // guard against any stale deploy where it comes back as JSON string or
      // an array of `{role|name|title|label|value}` objects.
      const flatten = (item) => {
        if (item == null) return null;
        if (typeof item === 'string') return item.trim() || null;
        if (typeof item === 'object') {
          const v = item.role || item.name || item.title || item.label || item.value;
          return v ? String(v).trim() : null;
        }
        return String(item).trim() || null;
      };
      let roles = raw.gig_roles;
      if (typeof roles === 'string') {
        try {
          const parsed = JSON.parse(roles);
          roles = Array.isArray(parsed) ? parsed : [];
        } catch {
          roles = roles ? roles.split(',').map((s) => s.trim()).filter(Boolean) : [];
        }
      }
      if (!Array.isArray(roles)) roles = [];
      roles = roles.map(flatten).filter(Boolean);
      setTalent({ ...raw, gig_roles: roles });
    } catch (e) {
      const status = e?.response?.status;
      const redirectSlug = e?.response?.data?.detail?.redirect_slug;
      if (status === 301 && redirectSlug && redirectSlug !== slug) {
        navigate(`/book/${redirectSlug}`, { replace: true });
        return;
      }
      // eslint-disable-next-line no-console
      console.error('Public booking fetch failed', slug, e?.response?.data || e.message);
      setTalent(null);
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // Analytics beacon — record a profile VIEW (with traffic source) so the
  // talent's Digital Business Card analytics stay accurate. Best-effort and
  // non-blocking; fires once per slug.
  useEffect(() => {
    if (!slug) return;
    try {
      const src = new URLSearchParams(window.location.search).get('src');
      axios.post(`${API}/api/booking/public/${slug}/track`, {
        event_type: 'view',
        src: src || null,
        referrer: (typeof document !== 'undefined' && document.referrer) || null,
      }).catch(() => {});
    } catch { /* noop */ }
  }, [slug]);

  // Personalized social preview (OG) for the clean /book/:slug URL. We
  // MUTATE the existing meta tags in place (never add duplicates) so the
  // production prerenderer — which executes this app and serializes the
  // final DOM for crawlers — hands Facebook/LinkedIn/WhatsApp a per-talent
  // og:image/title. The branded defaults in index.html are restored on
  // unmount so other pages keep their own preview.
  useEffect(() => {
    if (!talent || !slug) return;
    const origin = window.location.origin;
    const name = talent.display_name || 'this professional';
    const title = `Book ${name} · iEvntür`;
    // Use a PATH-versioned card URL (not a ?query) so Cloudflare, whose cache
    // key ignores query strings, treats each version as a brand-new object and
    // never serves a stale card. `c3` bumps when the card design changes; the
    // talent's own updated timestamp busts the cache when they edit their
    // profile/photo. The /api/og/v/<ver>/... route ignores the <ver> segment.
    const cardVer = (() => {
      const raw = talent.updated_at || talent.updatedAt || talent.profile_updated_at
        || talent.last_updated || talent.modified_at || '';
      const stamp = String(raw).replace(/[^0-9]/g, '').slice(0, 14);
      return `c3${stamp ? '-' + stamp : ''}`;
    })();
    const img = `${origin}/api/og/v/${cardVer}/card/${slug}.png`;
    const url = `${origin}/book/${slug}`;
    const roleHint = talent.primary_title ? ` · ${talent.primary_title}` : '';
    const bio = (talent.bio || '').trim();
    const loc = talent.public_location ? ` ${talent.public_location}.` : '';
    const desc = (bio.slice(0, 200) || `Book ${name} for your next event.${loc}${roleHint}`).trim();

    const prevTitle = document.title;
    const restore = [];
    const setMeta = (selector, value) => {
      const el = document.head.querySelector(selector);
      if (el) {
        restore.push([el, el.getAttribute('content')]);
        el.setAttribute('content', value);
      }
    };
    document.title = title;
    setMeta('meta[name="description"]', desc);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', desc);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[property="og:image"]', img);
    setMeta('meta[property="og:image:secure_url"]', img);
    setMeta('meta[property="og:image:alt"]', title);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', desc);
    setMeta('meta[name="twitter:image"]', img);
    return () => {
      document.title = prevTitle;
      restore.forEach(([el, val]) => { if (val != null) el.setAttribute('content', val); });
    };
  }, [talent, slug]);


  // Iter-P0-20260803 — Fetch trust + reviews stats. Runs independently
  // of the main profile fetch so a failure here never breaks the
  // booking form above.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/booking/public/${slug}/stats`);
        if (!cancelled) setProfileStats(r.data || null);
      } catch {
        if (!cancelled) setProfileStats(null);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Visual stepper progression. Step 1 unlocks once Event Details are
  // filled; step 2 unlocks when Your Info has the required pair.
  useEffect(() => {
    const eventReady = !!(form.event_date && form.event_details);
    const infoReady = !!(form.client_name && form.client_email);
    if (eventReady && infoReady) setStep(3);
    else if (eventReady) setStep(2);
    else setStep(1);
  }, [form.event_date, form.event_details, form.client_name, form.client_email]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.client_email || !form.event_date || !form.event_details) {
      toast.error('Please fill in required fields');
      return;
    }
    // Iter-291 — "Other Role Not Listed" requires a role-details note.
    if (form.roles_needed.includes('Other Role Not Listed') && !form.role_details.trim()) {
      toast.error('Please describe the role(s) you need in "Role Details"');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Iter-291 — send picked roles + optional details as structured fields
      // so backend analytics can be tracked properly. We still mirror them
      // into event_details so existing internal tooling (which reads the
      // free-text field) shows the same info.
      const tail = [];
      if (Array.isArray(payload.roles_needed) && payload.roles_needed.length > 0) {
        tail.push(`Roles needed: ${payload.roles_needed.join(', ')}`);
      }
      if (payload.role_details && payload.role_details.trim()) {
        tail.push(`Role details: ${payload.role_details.trim()}`);
      }
      if (tail.length > 0) {
        payload.event_details = `${payload.event_details}\n\n${tail.join('\n')}`;
      }
      payload.roles_selected = payload.roles_needed || [];
      payload.other_role_details = payload.role_details || '';
      delete payload.roles_needed;
      delete payload.role_details;
      // Attribution: carry the ?src=card_qr tag set by the business-card QR
      // tracker so bookings driven by the card are counted in Scan Insights.
      const _src = new URLSearchParams(window.location.search).get('src');
      if (payload.hourly_rate) payload.hourly_rate = parseFloat(payload.hourly_rate);
      if (payload.hours_estimated) payload.hours_estimated = parseFloat(payload.hours_estimated);
      Object.keys(payload).forEach((k) => {
        // Iter-291 — preserve roles_selected even when empty so the backend
        // analytics row is created consistently.
        if (k === 'roles_selected' || k === 'other_role_details') return;
        if (!payload[k]) delete payload[k];
      });
      if (_src) payload.source = _src;
      const r = await axios.post(`${API}/api/booking/public/${slug}/request`, payload);
      setSuccess(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit booking request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>;
  }

  if (!talent) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-xl font-semibold">Booking link not found</h1>
        <p className="text-sm text-gray-500 mt-2">This link is invalid or the talent is no longer available.</p>
        <Button className="mt-4" onClick={() => navigate('/')}>Go to iEvntür</Button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-cyan-50 to-white" data-testid="booking-success">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900">Request sent to {talent.display_name}</h1>
        <p className="text-sm text-gray-600 mt-2 max-w-md text-center">{success.message}</p>
        {success.magic_login_url && (
          <div className="mt-6 max-w-md w-full bg-violet-50 border border-violet-200 rounded-2xl p-5 text-center">
            <Sparkles className="w-6 h-6 text-violet-600 mx-auto mb-2" />
            <p className="text-sm text-gray-800 mb-2">Your secure sign-in link:</p>
            <a
              href={success.magic_login_url}
              className="text-xs text-violet-700 underline break-all"
              data-testid="magic-link-cta"
            >
              {success.magic_login_url}
            </a>
            <p className="text-[11px] text-gray-500 mt-2">Also sent to {form.client_email}.</p>
          </div>
        )}
      </div>
    );
  }

  const photo = resolveProfilePhoto(talent);
  const openLightbox = (index = 0) => setLightbox({ open: true, index });

  return (
    <div
      className="min-h-screen bg-[#f5f7fb] relative"
      data-testid="public-booking-page"
    >
      {/* Decorative gradient washes — keep the page light and airy without an
          outer border/container, matching the premium target design. */}
      <div className="absolute inset-x-0 top-0 h-[420px] pointer-events-none bg-gradient-to-b from-cyan-50/70 via-violet-50/40 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/*
         * Iter-291 — Mobile renders a single flex-column stack in the
         * user-requested order. Desktop renders two INDEPENDENT flex columns
         * side-by-side, each sized to its own content. This avoids the CSS
         * Grid row-balancing gap that appears when one column is much
         * taller than the other (e.g. complete profile + short form, or
         * sparse profile + long form). Cards are stateless closures over
         * parent state so React shares everything.
         */}
        {(() => {
          const headlineCard = (
            <div className="bg-white rounded-3xl shadow-[0_2px_28px_-12px_rgba(15,23,42,0.12)] border border-slate-100/80 p-5 sm:p-7 lg:p-8" data-testid="booking-headline-card">
              <div className="flex items-center gap-3 sm:gap-4 lg:gap-5">
                <div className="shrink-0 relative" aria-hidden>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-2xl bg-gradient-to-br from-cyan-100 to-cyan-50 border border-cyan-200/60 flex items-center justify-center shadow-sm">
                    <CalendarCheck className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-cyan-500" strokeWidth={2.25} />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-emerald-500 ring-2 ring-white flex items-center justify-center">
                    <CheckCircle2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" strokeWidth={3} />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h1
                    className="font-bold tracking-tight text-slate-900 leading-[1] whitespace-nowrap text-[26px] xs:text-[28px] sm:text-4xl lg:text-5xl"
                    data-testid="public-booking-headline"
                  >
                    Request a{' '}
                    <span
                      className="text-cyan-500 font-normal align-[-0.05em] text-[1.35em] sm:text-[1.4em]"
                      style={{ fontFamily: "'Great Vibes', cursive" }}
                    >
                      Booking
                    </span>
                  </h1>
                  <p className="text-[12.5px] sm:text-[14px] lg:text-[15px] text-slate-600 mt-1.5 sm:mt-2 leading-relaxed">
                    Fill out the details below to request {talent.display_name}.<br className="hidden sm:inline" />
                    We&apos;ll confirm rates &amp; availability and connect you.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 mt-5 sm:mt-6" data-testid="booking-trust-row">
                <TrustChip icon={ShieldCheck} title="Verified Talent" subtitle="Quality you can trust" />
                <TrustChip icon={Zap} title="Fast Response" subtitle="We reply quickly" />
                <TrustChip icon={Lock} title="Secure & Private" subtitle="Your info is safe" />
              </div>
            </div>
          );

          const profileCardEl = (
            <ProfileCard talent={talent} photo={photo} openLightbox={openLightbox} />
          );

          const formCardEl = (
            <div className="bg-white rounded-3xl shadow-[0_2px_28px_-12px_rgba(15,23,42,0.12)] border border-slate-100/80 p-6 sm:p-8" data-testid="booking-form-card">
              <Stepper step={step} />
              <form onSubmit={submit} className="mt-6 space-y-5" data-testid="booking-form">
                <FieldGroup label="Event Date" icon={Calendar} required>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <LabelledInput label="Event Date" required>
                      <Input type="date" required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="event-date-input" className="bg-slate-50/60" />
                    </LabelledInput>
                    <LabelledInput label="Start Time" iconLeft={<Clock className="w-3.5 h-3.5 text-slate-400" />}>
                      <Input type="time" value={form.event_start_time} onChange={(e) => setForm({ ...form, event_start_time: e.target.value })} className="bg-slate-50/60" />
                    </LabelledInput>
                    <LabelledInput label="End Time" iconLeft={<Clock className="w-3.5 h-3.5 text-slate-400" />}>
                      <Input type="time" value={form.event_end_time} onChange={(e) => setForm({ ...form, event_end_time: e.target.value })} className="bg-slate-50/60" />
                    </LabelledInput>
                  </div>
                </FieldGroup>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <LabelledInput label="City" iconLeft={<MapPin className="w-3.5 h-3.5 text-slate-400" />}>
                    <Input value={form.event_city} onChange={(e) => setForm({ ...form, event_city: e.target.value })} placeholder="Enter city" className="bg-slate-50/60" />
                  </LabelledInput>
                  <LabelledInput label="State" iconLeft={<MapPinned className="w-3.5 h-3.5 text-slate-400" />}>
                    <Input value={form.event_state} onChange={(e) => setForm({ ...form, event_state: e.target.value })} placeholder="e.g. FL" className="bg-slate-50/60" />
                  </LabelledInput>
                  <LabelledInput label="Venue Address" iconLeft={<Building2 className="w-3.5 h-3.5 text-slate-400" />}>
                    <Input value={form.venue_address} onChange={(e) => setForm({ ...form, venue_address: e.target.value })} placeholder="Enter venue address" className="bg-slate-50/60" />
                  </LabelledInput>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <LabelledInput label="Offered Rate ($/hr)" iconLeft={<DollarSign className="w-3.5 h-3.5 text-slate-400" />}>
                    <Input type="number" step="0.01" min="0" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="e.g. 100" className="bg-slate-50/60" />
                  </LabelledInput>
                  <LabelledInput label="Hours" iconLeft={<Clock className="w-3.5 h-3.5 text-slate-400" />}>
                    <Input type="number" step="0.5" min="0" value={form.hours_estimated} onChange={(e) => setForm({ ...form, hours_estimated: e.target.value })} placeholder="e.g. 4" className="bg-slate-50/60" />
                  </LabelledInput>
                </div>
                <LabelledInput label="Event Details" required iconLeft={<MessageSquare className="w-3.5 h-3.5 text-slate-400" />}>
                  <Textarea required rows={4} value={form.event_details} onChange={(e) => setForm({ ...form, event_details: e.target.value })} placeholder="What is the event? Dress code? Any specific requirements? Paste any links here — we'll auto-format them." data-testid="event-details-input" className="bg-slate-50/60" />
                </LabelledInput>
                <RoleSelector
                  selected={form.roles_needed}
                  onChange={(roles) => setForm((f) => ({ ...f, roles_needed: roles }))}
                  roleDetails={form.role_details}
                  onRoleDetailsChange={(v) => setForm((f) => ({ ...f, role_details: v }))}
                />
                <div className="pt-2 border-t border-slate-100">
                  <SectionHeader icon={UserIcon} title="Your Information" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <LabelledInput label="Your Name" required iconLeft={<UserIcon className="w-3.5 h-3.5 text-slate-400" />}>
                      <Input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Enter your full name" data-testid="client-name-input" className="bg-slate-50/60" />
                    </LabelledInput>
                    <LabelledInput label="Email" required iconLeft={<Mail className="w-3.5 h-3.5 text-slate-400" />}>
                      <Input type="email" required value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="you@example.com" data-testid="client-email-input" className="bg-slate-50/60" />
                    </LabelledInput>
                    <LabelledInput label="Phone (Optional)" iconLeft={<Phone className="w-3.5 h-3.5 text-slate-400" />}>
                      <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="(123) 456-7890" className="bg-slate-50/60" />
                    </LabelledInput>
                    <LabelledInput label="Company / Brand (Optional)" iconLeft={<Building2 className="w-3.5 h-3.5 text-slate-400" />}>
                      <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Enter company or brand" className="bg-slate-50/60" />
                    </LabelledInput>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-cyan-500 via-cyan-500 to-violet-500 hover:from-cyan-600 hover:via-cyan-600 hover:to-violet-600 text-white h-[52px] text-base font-semibold rounded-2xl shadow-[0_10px_30px_-12px_rgba(8,145,178,0.55)] hover:shadow-[0_12px_30px_-10px_rgba(124,58,237,0.5)] transition-all"
                  data-testid="submit-booking-btn"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
                  Send Booking Request
                </Button>
                <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 pt-1">
                  <Lock className="w-3 h-3" />
                  You&apos;ll receive a secure login link to track this request.
                </div>
              </form>
            </div>
          );

          const secureCardEl = (
            <div className="bg-white rounded-3xl shadow-[0_2px_24px_-12px_rgba(15,23,42,0.10)] border border-slate-100/80 p-6 sm:p-7 text-center" data-testid="secure-private-card">
              <div className="w-11 h-11 mx-auto rounded-full bg-gradient-to-br from-violet-100 to-violet-50 border border-violet-100 flex items-center justify-center mb-2 shadow-sm">
                <Lock className="w-5 h-5 text-violet-500" />
              </div>
              <div className="font-semibold text-slate-900 text-sm">Secure &amp; Private</div>
              <p className="text-[13px] text-slate-500 mt-1 leading-relaxed">
                Your information is encrypted<br />and never shared.
              </p>
              <svg className="mt-3 mx-auto text-cyan-400" width="32" height="10" viewBox="0 0 32 10" fill="none" aria-hidden>
                <path d="M1 5 Q 6 1, 11 5 T 21 5 T 31 5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
              </svg>
            </div>
          );

          if (isDesktop) {
            return (
              <div className="flex gap-8 items-start" data-testid="booking-layout-desktop">
                <div className="basis-7/12 space-y-5 min-w-0" data-testid="booking-left-col">
                  {headlineCard}
                  {formCardEl}
                </div>
                <div className="basis-5/12 space-y-5 min-w-0" data-testid="booking-right-col">
                  {profileCardEl}
                  {secureCardEl}
                </div>
              </div>
            );
          }
          return (
            <div className="space-y-5 sm:space-y-6" data-testid="booking-layout-mobile">
              {headlineCard}
              {profileCardEl}
              {formCardEl}
              {secureCardEl}
            </div>
          );
        })()}

        {/* Iter-P0-20260803 — Extended profile info shown BELOW existing
            layout. Preserves the current design; adds ratings, verified
            reviews, reliability, verified badges, skills, service areas. */}
        {profileStats && (
          <PublicBookingExtendedProfile
            stats={profileStats}
            talent={talent}
          />
        )}

        {/* Footer trust row — 2026-08-06 conditional: badges must
            reflect the actual talent record. Never label someone
            "Background checked" without a real background check, and
            never say "Top-rated" when there are no verified ratings. */}
        {(() => {
          const hasBGC = (profileStats?.badges || []).some(b => b.key === 'background_clear');
          const hasReviews = (profileStats?.total_reviews || 0) >= 1 && profileStats?.average_rating;
          const tiles = [];
          if (hasBGC) {
            tiles.push({ icon: ShieldCheck, title: 'Background Cleared', subtitle: 'Verified check on file' });
          }
          if (hasReviews) {
            tiles.push({
              icon: Star,
              title: `${profileStats.average_rating.toFixed(1)} ★ Rated`,
              subtitle: `${profileStats.total_reviews} verified review${profileStats.total_reviews === 1 ? '' : 's'}`,
            });
          }
          if ((profileStats?.completed_events || 0) > 0) {
            tiles.push({
              icon: Users,
              title: `${profileStats.completed_events} Event${profileStats.completed_events === 1 ? '' : 's'}`,
              subtitle: 'Completed on iEvntür',
            });
          }
          tiles.push({
            icon: MapPinned,
            title: 'Powered by iEvntür',
            subtitle: 'Book instantly + safely',
            href: IEVNTUR_URL,
            testid: 'booking-footer-powered-by-ievntur',
          });
          if (tiles.length === 0) return null;
          return (
            <div
              className={`mt-10 grid grid-cols-1 sm:grid-cols-${Math.min(tiles.length, 3)} gap-3 max-w-4xl mx-auto pb-4`}
              data-testid="booking-footer-trust"
            >
              {tiles.slice(0, 3).map((t, i) => (
                <FooterTrust
                  key={i}
                  icon={t.icon}
                  title={t.title}
                  subtitle={t.subtitle}
                  href={t.href}
                  testid={t.testid}
                />
              ))}
            </div>
          );
        })()}
      </div>

      <PhotoLightbox
        open={lightbox.open}
        images={[resolveProfilePhoto(talent), ...(Array.isArray(talent?.portfolio_images) ? talent.portfolio_images : [])].filter(Boolean)}
        index={lightbox.index}
        onClose={() => setLightbox({ open: false, index: 0 })}
        alt={talent?.display_name || 'Photo'}
      />
    </div>
  );
};

/* ───────────────────── presentational sub-components ───────────────────── */

/**
 * Iter-291 — Talent profile card body. Lives as its own component so the
 * mobile layout can place it ABOVE the booking form via grid `order-*`
 * utilities without duplicating the heavy markup.
 */
function ProfileCard({ talent, photo, openLightbox }) {
  return (
    <div className="bg-white rounded-3xl shadow-[0_2px_28px_-12px_rgba(15,23,42,0.12)] border border-slate-100/80 p-6 sm:p-8" data-testid="talent-profile-card">
      {/* Photo */}
      <div className="flex flex-col items-center text-center" data-testid="public-booking-header">
        <div className="relative">
          {photo ? (
            <button
              type="button"
              onClick={() => openLightbox(0)}
              className="rounded-full focus:outline-none focus:ring-2 focus:ring-cyan-400"
              aria-label="View profile photo"
              data-testid="public-booking-photo-btn"
            >
              <img
                src={photo}
                alt=""
                className="block w-36 h-36 sm:w-44 sm:h-44 rounded-full object-cover bg-white shadow-[0_8px_24px_-8px_rgba(15,23,42,0.18)] ring-4 ring-white cursor-pointer hover:opacity-95 transition"
                data-testid="public-booking-photo"
              />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => Array.isArray(talent.portfolio_images) && talent.portfolio_images.length > 0 ? openLightbox(0) : null}
              className="rounded-full focus:outline-none focus:ring-2 focus:ring-cyan-400"
              aria-label="Profile placeholder"
              data-testid="public-booking-photo-btn"
            >
              <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full bg-gradient-to-br from-cyan-200 to-violet-200 flex items-center justify-center text-5xl font-bold text-gray-700 shadow-[0_8px_24px_-8px_rgba(15,23,42,0.18)] ring-4 ring-white">
                {talent.display_name?.charAt(0)}
              </div>
            </button>
          )}
          {/* Verified shield badge — bottom-right of photo */}
          <div
            className="absolute -bottom-1 -right-1 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-500 ring-4 ring-white flex items-center justify-center shadow-md"
            aria-label="Verified talent"
            data-testid="public-booking-shield"
          >
            <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
        </div>

        <h2 className="mt-4 sm:mt-5 text-2xl sm:text-[34px] font-bold tracking-tight text-slate-900 inline-flex items-center gap-2" data-testid="public-booking-name">
          {talent.display_name}
          <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-cyan-500 shadow-sm">
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" strokeWidth={2.75} />
          </span>
        </h2>

        {talent.primary_title && (
          <p className="mt-1 text-sm sm:text-base font-semibold text-cyan-600" data-testid="public-booking-primary-title">
            {talent.primary_title}
          </p>
        )}

        {talent.public_location && (
          <p className="text-sm text-slate-500 inline-flex items-center gap-1.5 mt-1.5" data-testid="talent-public-location">
            <MapPin className="w-4 h-4 text-slate-400" /> {talent.public_location}
          </p>
        )}

        {/* Iter-411x — Available for Travel appears beneath market/location
            and BEFORE the hourly rate. Talent-opt-in only; rendered only
            when the flag is explicitly true so unset profiles stay clean. */}
        {talent.available_for_travel && (
          <p
            className="text-sm text-cyan-700 inline-flex items-center gap-1.5 mt-1.5"
            data-testid="talent-available-for-travel"
          >
            <Plane className="w-4 h-4 text-cyan-600" strokeWidth={2} /> Available for Travel
          </p>
        )}

        {talent.hourly_rate_min && (
          <div className="mt-4 inline-flex items-center gap-1.5 text-emerald-600 font-semibold text-[15px]" data-testid="talent-rate">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50">
              <DollarSign className="w-3.5 h-3.5" />
            </span>
            From ${talent.hourly_rate_min}/hr
          </div>
        )}

        {/* Iter-P0-20260803 — Rating summary shown DIRECTLY below the
            hourly rate on the profile card. If total_reviews is 0 we
            surface a "New to iEvntür" line instead of a fake zero-star. */}
        {talent.total_reviews > 0 && talent.average_rating ? (
          <div
            className="mt-2 inline-flex items-center gap-1.5 text-slate-800 text-[13px] font-semibold"
            data-testid="talent-rating-inline"
          >
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-3.5 h-3.5 ${n <= Math.round(talent.average_rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-100'}`}
                  strokeWidth={1.75}
                />
              ))}
            </div>
            <span>{talent.average_rating.toFixed(1)}</span>
            <span className="text-slate-500 font-normal">
              ({talent.total_reviews} Review{talent.total_reviews === 1 ? '' : 's'})
            </span>
          </div>
        ) : (
          <p
            className="mt-2 text-[12.5px] text-slate-500 italic max-w-xs"
            data-testid="talent-rating-new"
          >
            New to iEvntür. Be among the first to book this professional through our platform.
          </p>
        )}

        {talent.instagram && (
          <a
            href={`https://instagram.com/${talent.instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-pink-600 hover:text-pink-700 inline-flex items-center gap-1 text-xs mt-2"
          >
            <Instagram className="w-3.5 h-3.5" /> @{talent.instagram}
          </a>
        )}

        {talent.bio && (
          <p className="text-sm text-slate-700 leading-relaxed mt-3" data-testid="talent-bio">
            <LinkifiedText text={talent.bio} />
          </p>
        )}
      </div>

      {/* Iter-P0-20260803 — Intro video player. Native HTML5 <video> with
          controls, a poster image (talent photo), and a graceful
          fallback message when the browser can't play the format. Works
          on iOS Safari, Android Chrome, desktop Chrome/Safari/Firefox.
          `playsInline` prevents iOS from forcing full-screen playback. */}
      {talent.intro_video_url && (
        <div className="mt-5" data-testid="talent-intro-video">
          <video
            controls
            playsInline
            preload="metadata"
            poster={photo || undefined}
            className="w-full rounded-2xl border border-slate-200 bg-black shadow-sm"
            data-testid="talent-intro-video-player"
          >
            <source src={talent.intro_video_url} />
            <p className="p-4 text-sm text-slate-500 bg-slate-50 rounded-2xl">
              Your browser cannot play this intro video.
              {' '}
              <a
                href={talent.intro_video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:underline"
              >
                Open the video in a new tab.
              </a>
            </p>
          </video>
        </div>
      )}

      {/* Mini photo gallery */}
      {Array.isArray(talent.portfolio_images) && talent.portfolio_images.length > 0 && (
        <SectionBlock
          title={`Photos (${talent.portfolio_images.length})`}
          testId="public-booking-portfolio-section"
        >
          <div className="grid grid-cols-3 gap-2" data-testid="public-booking-portfolio-grid">
            {talent.portfolio_images.slice(0, 6).map((url, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  const startIndex = photo ? i + 1 : i;
                  openLightbox(startIndex);
                }}
                className="block aspect-square overflow-hidden rounded-xl bg-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-400 ring-1 ring-slate-200/70 hover:ring-cyan-300 transition"
                data-testid={`public-booking-photo-${i}`}
                aria-label={`View portfolio photo ${i + 1}`}
              >
                <img
                  src={url}
                  alt={`Portfolio ${i + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </SectionBlock>
      )}

      {/* Certifications */}
      {Array.isArray(talent.training_certifications) && talent.training_certifications.length > 0 && (
        <SectionBlock title="Certifications" testId="public-booking-certs-section">
          <div className="space-y-2.5">
            {talent.training_certifications.map((c, i) => (
              <CertCard key={i} cert={c} testIdPrefix={`public-booking-cert-${i}`} />
            ))}
          </div>
        </SectionBlock>
      )}

      {/* Roles */}
      {Array.isArray(talent.gig_roles) && talent.gig_roles.length > 0 && (
        <SectionBlock title="Roles">
          <div className="flex flex-wrap gap-1.5" data-testid="gig-roles-chips">
            {talent.gig_roles.map((r, i) => (
              <span
                key={i}
                className="text-[12.5px] py-1.5 px-3 bg-cyan-50 text-cyan-700 font-medium rounded-full hover:bg-cyan-100 transition-colors"
              >
                {r}
              </span>
            ))}
          </div>
        </SectionBlock>
      )}
    </div>
  );
}

function TrustChip({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-slate-200/70 bg-white px-3 py-2.5 hover:border-cyan-200 transition-colors">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-500 text-white flex items-center justify-center shrink-0 shadow-sm">
        <Icon className="w-4 h-4" strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold text-slate-900 leading-tight">{title}</div>
        <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
}

function CertCard({ cert, testIdPrefix }) {
  // Iter-291 — defensive: supports the canonical shape returned by
  // utils/talent_certifications.py (`badge_name` + `module_name` + `score`
  // + `passed_at`) as well as a few legacy shapes.
  const name = cert?.badge_name || cert?.module_name || cert?.name || cert?.title || cert?.role || cert?.cert_name || 'Certification';
  const score = cert?.score ?? cert?.percentage ?? cert?.percent;
  const earned = cert?.passed_at || cert?.earned_at || cert?.completed_at || cert?.earned_date || cert?.date;
  const earnedDate = earned ? new Date(earned).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-') : null;
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-cyan-100 bg-cyan-50/40 px-4 py-3" data-testid={testIdPrefix}>
      <div className="w-9 h-9 shrink-0 rounded-full bg-white border border-cyan-200/70 flex items-center justify-center shadow-sm">
        <ShieldCheck className="w-4 h-4 text-cyan-600" strokeWidth={2.25} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-slate-900 leading-tight">
          {name}{typeof score === 'number' ? ` — ${score}%` : ''}
        </div>
        {earnedDate && (
          <div className="text-[12px] text-slate-500 mt-0.5 leading-tight">Earned {earnedDate}</div>
        )}
      </div>
    </div>
  );
}

/**
 * Iter-291 — Fixed 6-option role picker for the public booking form.
 *
 * Always shows the same six roles regardless of which talent is being
 * booked, keeping the booking experience consistent and fast. The last
 * option, "Other Role Not Listed", reveals a required free-text
 * description so clients can request any niche role (Mascot, Greeter,
 * Production Assistant, etc.) without cluttering the picker.
 */
const ROLE_OPTIONS = [
  { key: 'Brand Ambassador',          icon: Megaphone,       popular: true  },
  { key: 'Event Staff',               icon: Users,           popular: false },
  { key: 'Bartender',                 icon: Wine,            popular: false },
  { key: 'Influencer / Content Creator', icon: Camera,       popular: false },
  { key: 'Hospitality',               icon: HandHeart,       popular: false },
  { key: 'Other Role Not Listed',     icon: MoreHorizontal,  popular: false },
];

function RoleSelector({ selected, onChange, roleDetails, onRoleDetailsChange }) {
  const toggle = (role) => {
    const set = new Set(selected);
    if (set.has(role)) set.delete(role); else set.add(role);
    onChange(Array.from(set));
  };
  const otherSelected = selected.includes('Other Role Not Listed');

  return (
    <div data-testid="roles-needed-selector">
      <div className="flex items-center justify-between mb-3 mt-1">
        <div>
          <div className="text-sm font-bold text-slate-900">Select the role(s) you need</div>
          <div className="text-[12px] text-slate-500 mt-0.5">Pick one or more — choose &quot;Other&quot; if you don&apos;t see the role.</div>
        </div>
        {selected.length > 0 && (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-violet-600 bg-violet-50 px-2 py-1 rounded-full">
            {selected.length} selected
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {ROLE_OPTIONS.map(({ key, icon: Icon, popular }, i) => {
          const on = selected.includes(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              className={`relative text-left rounded-xl border px-3 pt-6 pb-3 transition-all ${
                on
                  ? 'border-violet-400 bg-violet-50/60 ring-2 ring-violet-100 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-cyan-200 hover:bg-cyan-50/30'
              }`}
              data-testid={`role-card-${i}`}
            >
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  on ? 'bg-violet-100 text-violet-600' : 'bg-cyan-50 text-cyan-600'
                }`}>
                  <Icon className="w-4 h-4" strokeWidth={2.25} />
                </div>
                <div className="text-[12.5px] font-semibold text-slate-800 leading-tight">{key}</div>
              </div>
              {popular && !on && (
                <div className="absolute top-1 left-1 inline-flex items-center gap-0.5 text-[9.5px] font-bold text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded-full uppercase tracking-wider" data-testid="role-card-popular-badge">
                  <Star className="w-2.5 h-2.5 fill-amber-400 stroke-amber-500" />
                  Popular
                </div>
              )}
              {on && (
                <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-violet-500 flex items-center justify-center shadow-sm">
                  <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {otherSelected && (
        <div className="mt-3" data-testid="role-details-block">
          <LabelledInput label="Role Details" required iconLeft={<MessageSquare className="w-3.5 h-3.5 text-slate-400" />}>
            <Textarea
              required
              rows={3}
              value={roleDetails || ''}
              onChange={(e) => onRoleDetailsChange(e.target.value)}
              placeholder="Describe the role(s) you're looking for — e.g. Load-In / Load-Out, Team Captain, Mascot, Ticket Scanner, Greeter, Street Team, Production Assistant, Product Specialist, Event Manager, Server, etc."
              data-testid="role-details-input"
              className="bg-slate-50/60"
            />
          </LabelledInput>
        </div>
      )}
    </div>
  );
}

function Stepper({ step }) {
  const items = [
    { n: 1, label: 'Event Details' },
    { n: 2, label: 'Your Info' },
    { n: 3, label: 'Review & Send' },
  ];
  return (
    <ol className="flex items-center gap-2 text-xs" data-testid="booking-stepper">
      {items.map((it, idx) => {
        const active = step === it.n;
        const done = step > it.n;
        return (
          <React.Fragment key={it.n}>
            <li className={`flex items-center gap-2 ${active ? 'text-cyan-700' : done ? 'text-slate-500' : 'text-slate-400'}`}>
              <span
                className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold border ${
                  active
                    ? 'bg-cyan-500 text-white border-cyan-500 shadow-sm'
                    : done
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-white text-slate-400 border-slate-200'
                }`}
              >
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : it.n}
              </span>
              <span className={`hidden sm:inline font-medium ${active ? 'text-slate-900' : ''}`}>{it.label}</span>
            </li>
            {idx < items.length - 1 && (
              <div className="flex-1 h-px border-t border-dashed border-slate-200 mx-1" aria-hidden />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

function LabelledInput({ label, required, iconLeft, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12.5px] font-semibold text-slate-700 tracking-wide">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </Label>
      <div className="relative">
        {iconLeft && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">{iconLeft}</span>
        )}
        <div className={iconLeft ? '[&_input]:pl-9 [&_input]:h-11 [&_input]:rounded-xl [&_input]:border-slate-200 [&_input]:bg-slate-50/70 [&_input]:focus-visible:bg-white [&_textarea]:pl-9 [&_textarea]:rounded-xl [&_textarea]:border-slate-200 [&_textarea]:bg-slate-50/70 [&_textarea]:focus-visible:bg-white' : '[&_input]:h-11 [&_input]:rounded-xl [&_input]:border-slate-200 [&_input]:bg-slate-50/70 [&_input]:focus-visible:bg-white [&_textarea]:rounded-xl [&_textarea]:border-slate-200 [&_textarea]:bg-slate-50/70 [&_textarea]:focus-visible:bg-white'}>
          {children}
        </div>
      </div>
    </div>
  );
}

function FieldGroup({ children }) {
  return <div>{children}</div>;
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-3.5 mt-1">
      <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
      </div>
      <h3 className="text-[15px] font-bold text-slate-900 tracking-tight">{title}</h3>
    </div>
  );
}

function SectionBlock({ title, testId, children }) {
  return (
    <div className="mt-6 pt-6 border-t border-slate-100" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}

function FooterTrust({ icon: Icon, title, subtitle, href, testid }) {
  // Iter-411h — when a tile carries an `href` (currently only the
  // "Powered by iEvntür" tile) the ENTIRE card becomes a single link
  // that opens the marketing site in a new tab. Reuses IEVNTUR_URL
  // from PoweredByFooter so we never duplicate the destination.
  const body = (
    <>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-100 to-cyan-50 border border-cyan-100 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-cyan-600" strokeWidth={2.25} />
      </div>
      <div>
        <div className="text-[13.5px] font-bold text-slate-900 leading-tight">{title}</div>
        <div className="text-[12px] text-slate-500 leading-tight mt-0.5">{subtitle}</div>
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${title} — ${subtitle} (opens iEvntür website)`}
        data-testid={testid || 'footer-trust-link'}
        className="flex items-center gap-3 rounded-2xl bg-white shadow-[0_2px_18px_-12px_rgba(15,23,42,0.18)] border border-slate-100/80 px-5 py-4 hover:border-cyan-300 hover:shadow-[0_4px_22px_-10px_rgba(6,182,212,0.28)] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 cursor-pointer no-underline"
      >
        {body}
      </a>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-white shadow-[0_2px_18px_-12px_rgba(15,23,42,0.18)] border border-slate-100/80 px-5 py-4 hover:border-cyan-200 transition-colors">
      {body}
    </div>
  );
}

/* ─────────────── Iter-P0-20260803 — Extended profile info ────────────────
 * Added BELOW the existing booking-page layout (never on top of / replacing
 * it). Renders overall rating, verified reviews, trust / reliability tiles,
 * verified badges, skills + service areas, response time, and testimonials.
 * Reviews come from `talent_ratings` joined to completed bookings only —
 * cancelled or incomplete bookings can never generate a review.
 *
 * Empty-state safe: if the talent has zero verified reviews, we NEVER
 * render a fake zero-star rating. Instead we show a "New to iEvntür" hero
 * so new professionals aren't penalised.
 */
function PublicBookingExtendedProfile({ stats, talent }) {
  const s = stats || {};
  const hasReviews = (s.total_reviews || 0) >= 1 && (s.average_rating || 0) > 0;
  const badges = Array.isArray(s.badges) ? s.badges : [];
  const skills = Array.isArray(s.skills) ? s.skills : [];
  const markets = Array.isArray(s.markets_served) ? s.markets_served : [];
  const reviews = Array.isArray(s.reviews) ? s.reviews : [];

  const displayName = talent?.display_name || 'This professional';

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    } catch { return ''; }
  };

  const roundedRating = hasReviews ? s.average_rating.toFixed(1) : null;
  const relPct = s.reliability_score != null
    ? Math.round(s.reliability_score) // 0-100 scale in the DB
    : null;
  const responseCopy = (() => {
    const hrs = s.avg_response_hours;
    if (hrs == null) return null;
    if (hrs < 1) return 'Usually responds within an hour';
    if (hrs < 3) return `Usually responds in ~${Math.round(hrs)} hours`;
    if (hrs < 24) return `Usually responds within ${Math.round(hrs)} hours`;
    return `Usually responds within ${Math.round(hrs / 24)} day${hrs >= 48 ? 's' : ''}`;
  })();

  return (
    <section
      className="mt-8 sm:mt-10 max-w-4xl mx-auto space-y-6"
      data-testid="public-booking-extended"
    >
      {/* Rating hero */}
      <div
        className="rounded-2xl border border-slate-100 bg-white shadow-[0_2px_20px_-14px_rgba(15,23,42,0.18)] p-5 sm:p-7"
        data-testid="extended-rating-hero"
      >
        {hasReviews ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-6 h-6 ${n <= Math.round(s.average_rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-100'}`}
                    strokeWidth={1.75}
                  />
                ))}
              </div>
              <div>
                <div className="text-2xl font-black text-slate-900 leading-none">{roundedRating}</div>
                <div className="text-[12px] text-slate-500 mt-0.5">
                  {s.total_reviews} verified review{s.total_reviews === 1 ? '' : 's'}
                </div>
              </div>
            </div>
            <div className="hidden sm:block h-10 w-px bg-slate-200" />
            <div className="text-[13px] text-slate-600 leading-snug">
              All reviews come from clients who <strong>completed</strong> a booking with {displayName} on iEvntür.
              Cancelled or incomplete bookings never generate a review.
            </div>
          </div>
        ) : (
          <div className="text-center py-2" data-testid="extended-new-to-platform">
            <Sparkles className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
            <div className="text-lg font-bold text-slate-900">New to iEvntür</div>
            <div className="text-[13px] text-slate-500 mt-1 max-w-md mx-auto">
              Be among the first to book {displayName} through our platform. All future
              reviews will come from verified completed bookings.
            </div>
          </div>
        )}
      </div>

      {/* Trust / reliability tiles */}
      {(s.completed_events > 0 || relPct != null || s.repeat_client_pct != null || responseCopy) && (
        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
          data-testid="extended-trust-tiles"
        >
          {s.completed_events > 0 && (
            <StatTile
              icon={CheckCircle2}
              label="Events completed"
              value={s.completed_events}
              accent="emerald"
              testid="tile-events-completed"
            />
          )}
          {relPct != null && (
            <StatTile
              icon={ShieldCheck}
              label="Reliability"
              value={`${relPct}%`}
              accent="cyan"
              testid="tile-reliability"
            />
          )}
          {s.repeat_client_pct != null && s.repeat_client_pct > 0 && (
            <StatTile
              icon={Users}
              label="Repeat clients"
              value={`${Math.round(s.repeat_client_pct)}%`}
              accent="violet"
              testid="tile-repeat-clients"
            />
          )}
          {responseCopy && (
            <StatTile
              icon={Zap}
              label="Response time"
              value={responseCopy.replace(/^Usually responds /, '')}
              accent="amber"
              testid="tile-response-time"
            />
          )}
        </div>
      )}

      {/* Verified badges */}
      {badges.length > 0 && (
        <div
          className="rounded-2xl border border-slate-100 bg-white p-5"
          data-testid="extended-badges"
        >
          <SectionTitle title="Verified" />
          <div className="flex flex-wrap gap-2 mt-3">
            {badges.map((b) => (
              <span
                key={b.key}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full"
                data-testid={`badge-${b.key}`}
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                {b.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Skills section removed 2026-08-06 — the top "Roles" chips on
          the profile card are the single source of truth. The previous
          Skills & Roles section duplicated them AND leaked raw JSON
          objects (`{'role': 'Guest Registration', 'category':
          'event_staffing'}`) from `role_attributes`. If iEvntür adds a
          dedicated skills column in the future, plug it back in here. */}

      {/* Service areas */}
      {markets.length > 0 && (
        <div
          className="rounded-2xl border border-slate-100 bg-white p-5"
          data-testid="extended-markets"
        >
          <SectionTitle title="Service Areas" />
          <div className="flex flex-wrap gap-1.5 mt-3">
            {markets.map((m) => (
              <span
                key={m}
                className="inline-flex items-center gap-1 text-[12.5px] font-medium text-cyan-800 bg-cyan-50 border border-cyan-100 px-2.5 py-1 rounded-full"
              >
                <MapPin className="w-3 h-3" />
                {m}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Reviews list */}
      {hasReviews && reviews.length > 0 && (
        <div
          className="rounded-2xl border border-slate-100 bg-white p-5"
          data-testid="extended-reviews"
        >
          <SectionTitle title={`Recent client reviews (${s.total_reviews})`} />
          <div className="mt-3 space-y-4">
            {reviews.slice(0, 6).map((rv, i) => (
              <div
                key={i}
                className="pb-4 border-b border-slate-100 last:border-b-0 last:pb-0"
                data-testid={`review-item-${i}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={`w-3.5 h-3.5 ${n <= (rv.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-100'}`}
                        strokeWidth={1.5}
                      />
                    ))}
                    <span className="ml-1.5 text-[12.5px] font-semibold text-slate-700">
                      {rv.author}
                    </span>
                    <span className="ml-1 text-[11px] text-slate-400 inline-flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3 text-emerald-500" /> Verified booking
                    </span>
                  </div>
                  <span className="text-[11px] text-slate-400 shrink-0">{formatDate(rv.date)}</span>
                </div>
                {rv.feedback && (
                  <p className="text-[13px] text-slate-700 mt-1.5 leading-relaxed">&ldquo;{rv.feedback}&rdquo;</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function StatTile({ icon: Icon, label, value, accent = 'slate', testid }) {
  const tone = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-100',
    violet: 'bg-violet-50 text-violet-700 border-violet-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-100',
  }[accent] || 'bg-slate-50 text-slate-700 border-slate-100';
  return (
    <div
      className="rounded-2xl border border-slate-100 bg-white p-4 flex items-start gap-3"
      data-testid={testid}
    >
      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-lg font-bold text-slate-900 leading-tight truncate">{value}</div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5 truncate">{label}</div>
      </div>
    </div>
  );
}

function SectionTitle({ title }) {
  return (
    <h3 className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-bold">
      {title}
    </h3>
  );
}

export default PublicBooking;
